import {
  hasAnyRole
} from "./chunk-GL2JF7ZX.js";
import {
  useAuth,
  useGatewayAuthority,
  useGatewayPermission
} from "./chunk-RJWLBNM2.js";
import {
  AuthContext
} from "./chunk-52JZDGH7.js";

// src/react/AuthProvider.tsx
import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { jsx } from "react/jsx-runtime";
function AuthProvider({
  manager,
  children
}) {
  const [state, setState] = useState(manager.getState());
  useEffect(() => {
    const unsubscribe = manager.subscribe((newState) => {
      setState(newState);
    });
    manager.initialize().catch(() => {
      setState(manager.getState());
    });
    return () => {
      unsubscribe();
      manager.dispose();
    };
  }, [manager]);
  const login = useCallback(async () => {
    await manager.login();
  }, [manager]);
  const logout = useCallback(async () => {
    await manager.logout();
  }, [manager]);
  const getAccessToken = useCallback(async () => {
    return manager.getAccessToken();
  }, [manager]);
  const handleCallback = useCallback(async () => {
    await manager.handleCallback();
  }, [manager]);
  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
      getAccessToken,
      handleCallback
    }),
    [state, login, logout, getAccessToken, handleCallback]
  );
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value, children });
}

// src/react/useAuthGuard.ts
import { useMemo as useMemo2 } from "react";
function useAuthGuardState(requiredRoles) {
  const authority = useGatewayAuthority();
  const gateway = useGatewayPermission(requiredRoles);
  const { user } = useAuth();
  const localGranted = useMemo2(() => hasAnyRole(user, [...requiredRoles]), [user, requiredRoles]);
  return authority ? gateway : { granted: localGranted, loading: false, authoritative: false, error: false };
}
function useAuthGuard(requiredRoles) {
  return useAuthGuardState(requiredRoles).granted;
}

// src/react/ProtectedRoute.tsx
import { useEffect as useEffect2 } from "react";
import { Fragment, jsx as jsx2 } from "react/jsx-runtime";
function ProtectedRoute({ children, returnToKey = "oidc.returnTo" }) {
  const { isAuthenticated, isLoading, login } = useAuth();
  useEffect2(() => {
    if (!isLoading && !isAuthenticated) {
      try {
        sessionStorage.setItem(returnToKey, window.location.pathname + window.location.search);
      } catch {
      }
      login().catch(() => {
      });
    }
  }, [isAuthenticated, isLoading, login, returnToKey]);
  if (isLoading) {
    return null;
  }
  if (!isAuthenticated) {
    return null;
  }
  return /* @__PURE__ */ jsx2(Fragment, { children });
}

// src/react/OidcCallbackHandler.tsx
import { useCallback as useCallback2, useEffect as useEffect3, useRef, useState as useState2 } from "react";
function OidcCallbackHandler({
  defaultRedirectPath = "/",
  returnToKey = "oidc.returnTo",
  navigate,
  renderLoading,
  renderError
}) {
  const { handleCallback } = useAuth();
  const [state, setState] = useState2("loading");
  const [error, setError] = useState2("");
  const handledRef = useRef(false);
  const processCallback = useCallback2(async () => {
    try {
      setState("loading");
      setError("");
      await handleCallback();
      let returnTo = defaultRedirectPath;
      try {
        const saved = sessionStorage.getItem(returnToKey);
        if (saved) {
          returnTo = saved;
          sessionStorage.removeItem(returnToKey);
        }
      } catch {
      }
      setState("success");
      navigate(returnTo);
    } catch (err) {
      console.error("OIDC callback failed:", err instanceof Error ? err.message : err);
      setState("error");
      setError("Authentication failed. Please try again.");
    }
  }, [handleCallback, defaultRedirectPath, returnToKey, navigate]);
  useEffect3(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    processCallback();
  }, [processCallback]);
  if (state === "loading") {
    return renderLoading?.() ?? null;
  }
  if (state === "error") {
    return renderError?.(error, processCallback) ?? null;
  }
  return null;
}

export {
  AuthProvider,
  useAuthGuardState,
  useAuthGuard,
  ProtectedRoute,
  OidcCallbackHandler
};
//# sourceMappingURL=chunk-5YHAOPYV.js.map